import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { TripDisplay } from "../components/TripDisplay";
import { CollabPanel } from "../components/CollabPanel";
import { PublicShareControl } from "../components/PublicShareControl";
import { track } from "../lib/analytics";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";
import { ArrowLeft, Share2, Undo2, History, X, RotateCcw, Lock, Link2, Globe, Sparkles, Loader2, Copy } from "lucide-react";

const VIS_OPTS = [
  { key: "private", label: "Private", icon: Lock, hint: "Only you can see this trip." },
  { key: "link", label: "Anyone with link", icon: Link2, hint: "Anyone with the link can view (no login)." },
  { key: "public", label: "Public", icon: Globe, hint: "Viewable by anyone and shareable openly." },
];

export default function TripView() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState("");
  const [undoing, setUndoing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [hasHistory, setHasHistory] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [visibility, setVisibility] = useState("private");
  const [shareId, setShareId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pz, setPz] = useState(null); // personalize form state or null
  const [pzLoading, setPzLoading] = useState(false);
  const saveTimer = useRef();

  const refreshVersions = () => {
    api.get(`/trips/${id}/versions`).then((res) => {
      setVersions(res.data);
      setHasHistory(res.data.length > 0);
    }).catch(() => {});
  };

  useEffect(() => {
    api
      .get(`/trips/${id}`)
      .then((res) => {
        setTrip(res.data.trip);
        if (searchParams.get("personalize") === "1") {
          const r = res.data.trip._request || {};
          setPz({
            travel_start_date: r.travel_start_date || "",
            target_budget: r.target_budget || "",
            travelers: r.travelers || "",
            interests: r.interests || "",
          });
        }
      })
      .catch(() => setError("Trip not found"));
    refreshVersions();
    api.get(`/trips/${id}/share-status`).then((r) => {
      setShareId(r.data.share_id);
      setVisibility(r.data.visibility || "private");
    }).catch(() => {});
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTripChange = (newTrip, opts) => {
    setTrip(newTrip);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/trips/${id}`, { trip: newTrip, label: opts?.label })
        .then(refreshVersions)
        .catch(() => toast.error("Couldn't save your change. Please try again."));
    }, 800);
  };

  const undo = async () => {
    setUndoing(true);
    clearTimeout(saveTimer.current);
    try {
      const { data } = await api.post(`/trips/${id}/undo`);
      setTrip(data.trip);
      refreshVersions();
      toast.success("Reverted your last change");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nothing to undo");
    } finally {
      setUndoing(false);
    }
  };

  const restore = async (versionId) => {
    clearTimeout(saveTimer.current);
    try {
      const { data } = await api.post(`/trips/${id}/restore/${versionId}`);
      setTrip(data.trip);
      setShowHistory(false);
      refreshVersions();
      toast.success("Restored that version");
    } catch {
      toast.error("Couldn't restore that version");
    }
  };

  const setShareVisibility = async (vis) => {
    setVisibility(vis);
    try {
      const { data } = await api.post(`/trips/${id}/share`, { visibility: vis });
      setShareId(data.share_id);
    } catch {
      toast.error("Couldn't update sharing.");
    }
  };

  const copyShareLink = async () => {
    if (!shareId) return;
    const url = `${window.location.origin}/shared/${shareId}`;
    track("trip_shared", { method: "collab_link" });
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.success(`Share link: ${url}`);
    }
  };

  const runPersonalize = async () => {
    setPzLoading(true);
    try {
      const { data } = await api.post("/trips/personalize", { trip, ...pz });
      setTrip(data.trip);
      api.put(`/trips/${id}`, { trip: data.trip, label: "Personalised copy" }).then(refreshVersions).catch(() => {});
      setPz(null);
      setSearchParams({});
      toast.success(data.summary || "Personalised your copy!");
    } catch {
      toast.error("Couldn't personalise right now — your preferences were still saved.");
    } finally {
      setPzLoading(false);
    }
  };

  const fmtDate = (s) => {
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  return (
    <div className="min-h-screen grain relative">
      <Seo title={trip?.custom_name || trip?.destination || "My trip"} description="Your saved WanderlyMe itinerary — edit, optimise routes, track budget and share with travel companions." path={`/trips/${id}`} noindex />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="no-print flex items-center justify-between gap-3 mb-8 flex-wrap">
          <Link
            to="/trips"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[#C8553D] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to my trips
          </Link>
          {trip && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="trip-undo-btn"
                onClick={undo}
                disabled={undoing || !hasHistory}
                title={hasHistory ? "Undo last change" : "Nothing to undo yet"}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/15 text-[#1A3B2B] hover:bg-black/5 transition-colors disabled:opacity-40"
              >
                <Undo2 className="w-4 h-4" /> Undo
              </button>
              <button
                data-testid="trip-history-btn"
                onClick={() => { refreshVersions(); setShowHistory(true); }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/15 text-[#1A3B2B] hover:bg-black/5 transition-colors"
              >
                <History className="w-4 h-4" /> History
              </button>
              <button
                data-testid="trip-share-btn"
                onClick={() => setShowShare((s) => !s)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors"
              >
                <Share2 className="w-4 h-4" /> Share trip
              </button>
            </div>
          )}
        </div>
        {error ? (
          <p className="text-[#C8553D]">{error}</p>
        ) : !trip ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <>
            <TripDisplay trip={trip} editable onTripChange={handleTripChange} />
            {shareId && visibility !== "private" && (
              <CollabPanel shareId={shareId} trip={trip} />
            )}
          </>
        )}
      </div>

      {/* Share settings panel */}
      {showShare && (
        <div className="no-print fixed inset-0 z-[950] flex items-start justify-center pt-24 px-4" data-testid="share-panel">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowShare(false)} />
          <div className="relative w-full max-w-md bg-[#FDFBF7] rounded-3xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl inline-flex items-center gap-2"><Share2 className="w-5 h-5 text-[#C8553D]" /> Share this trip</h3>
              <button data-testid="share-close-btn" onClick={() => setShowShare(false)} className="p-1.5 rounded-full hover:bg-black/5"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2">
              {VIS_OPTS.map((o) => (
                <button
                  key={o.key}
                  data-testid={`share-vis-${o.key}`}
                  onClick={() => setShareVisibility(o.key)}
                  className={`w-full text-left flex items-start gap-3 p-3.5 rounded-2xl border transition-colors ${visibility === o.key ? "border-[#1A3B2B] bg-[#1A3B2B]/5" : "border-black/10 hover:bg-black/5"}`}
                >
                  <o.icon className={`w-5 h-5 mt-0.5 ${visibility === o.key ? "text-[#C8553D]" : "text-muted-foreground"}`} />
                  <span>
                    <span className="block font-medium text-sm">{o.label}</span>
                    <span className="block text-xs text-muted-foreground">{o.hint}</span>
                  </span>
                </button>
              ))}
            </div>
            {visibility !== "private" && shareId && (
              <div className="mt-4">
                <div className="flex items-center gap-2 p-2 rounded-full border border-black/10 bg-white">
                  <input readOnly data-testid="share-link-input" value={`${window.location.origin}/shared/${shareId}`} className="flex-1 min-w-0 bg-transparent text-sm px-2 outline-none" />
                  <button data-testid="share-copy-btn" onClick={copyShareLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1A3B2B] text-[#FDFBF7] text-sm shrink-0"><Copy className="w-3.5 h-3.5" /> Copy</button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Viewers don't need an account and can copy your trip to personalise their own.</p>
              </div>
            )}
            <PublicShareControl tripId={id} />
          </div>
        </div>
      )}

      {/* Personalise copied trip modal */}
      {pz && (
        <div className="no-print fixed inset-0 z-[950] flex items-start justify-center pt-16 px-4 overflow-y-auto" data-testid="personalize-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setPz(null); setSearchParams({}); }} />
          <div className="relative w-full max-w-lg bg-[#FDFBF7] rounded-3xl shadow-2xl p-6 my-8">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-[#C8553D]" />
              <h3 className="font-display text-2xl">Make it yours</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Adjust the details and WanderlyMe will personalise this copied itinerary — the original is untouched.</p>
            <div className="space-y-3">
              <label className="block text-sm">Trip start date
                <input data-testid="pz-date" type="date" value={pz.travel_start_date} onChange={(e) => setPz({ ...pz, travel_start_date: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-black/10 bg-white outline-none focus:border-[#1A3B2B]" />
              </label>
              <label className="block text-sm">Budget (total)
                <input data-testid="pz-budget" value={pz.target_budget} onChange={(e) => setPz({ ...pz, target_budget: e.target.value })} placeholder="e.g. 2000 USD" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-black/10 bg-white outline-none focus:border-[#1A3B2B]" />
              </label>
              <label className="block text-sm">Travellers
                <input data-testid="pz-travelers" value={pz.travelers} onChange={(e) => setPz({ ...pz, travelers: e.target.value })} placeholder="e.g. 2 adults, 1 child" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-black/10 bg-white outline-none focus:border-[#1A3B2B]" />
              </label>
              <label className="block text-sm">Interests
                <input data-testid="pz-interests" value={pz.interests} onChange={(e) => setPz({ ...pz, interests: e.target.value })} placeholder="e.g. food, art, hiking, nightlife" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-black/10 bg-white outline-none focus:border-[#1A3B2B]" />
              </label>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <button data-testid="pz-submit" onClick={runPersonalize} disabled={pzLoading} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#12291e] transition-colors disabled:opacity-60">
                {pzLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {pzLoading ? "Personalising…" : "Personalise with AI"}
              </button>
              <button data-testid="pz-skip" onClick={() => { setPz(null); setSearchParams({}); }} className="px-5 py-3 rounded-full border border-black/15 text-sm hover:bg-black/5 transition-colors">Skip for now</button>
            </div>
          </div>
        </div>
      )}

      {/* Version history panel */}
      {showHistory && (
        <div className="no-print fixed inset-0 z-[950] flex justify-end" data-testid="history-panel">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowHistory(false)} />
          <div className="relative w-[min(420px,100vw)] h-full bg-[#FDFBF7] shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
              <span className="font-display text-xl inline-flex items-center gap-2"><History className="w-5 h-5 text-[#C8553D]" /> Version history</span>
              <button data-testid="history-close-btn" onClick={() => setShowHistory(false)} className="p-1.5 rounded-full hover:bg-black/5"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <p className="text-sm text-muted-foreground">Every edit is saved automatically. Restore any earlier version — restoring is itself undoable.</p>
              {versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No previous versions yet. Make a change and it'll appear here.</p>
              ) : (
                versions.map((v) => (
                  <div key={v.id} data-testid="history-item" className="flex items-center justify-between gap-3 p-4 rounded-2xl border border-black/5 bg-white/60">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{v.label}</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(v.created_at)} · {v.days} days</p>
                    </div>
                    <button
                      data-testid="history-restore-btn"
                      onClick={() => restore(v.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1A3B2B] text-[#1A3B2B] text-xs hover:bg-[#1A3B2B] hover:text-[#FDFBF7] transition-colors shrink-0"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
